import { Info } from 'lucide-react';
import type { SourceDescriptor, SourceSystem } from '../../shared/contracts';

const SYSTEM_META: Record<SourceSystem, { label: string; dot: string }> = {
  superoffice: { label: 'SuperOffice', dot: 'bg-brand-blue' },
  'google-analytics': { label: 'Google Analytics', dot: 'bg-brand-amber' },
  activecampaign: { label: 'ActiveCampaign', dot: 'bg-brand-accent' },
  computed: { label: 'Beregnet', dot: 'bg-brand-dark/40' },
};

/** Keyboard-accessible source indicator: reveals the data source + caveats on hover or focus. */
export function SourceBadge({ source }: { source: SourceDescriptor }) {
  const meta = SYSTEM_META[source.system];
  return (
    <div className="group/src relative">
      <button
        type="button"
        className="flex items-center gap-1 rounded text-brand-dark/35 transition-colors hover:text-brand-primary focus:text-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50"
        aria-label={`Datakilde: ${meta.label}. ${source.detail}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
        <Info size={13} />
      </button>
      <div
        role="tooltip"
        className="invisible absolute right-0 top-5 z-30 w-64 rounded-brand border border-brand-primary/15 bg-white p-3 text-left opacity-0 shadow-xl transition-opacity group-hover/src:visible group-hover/src:opacity-100 group-focus-within/src:visible group-focus-within/src:opacity-100"
      >
        <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-brand-primary">
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} aria-hidden />
          <span>Datakilde: {meta.label}</span>
        </div>
        <p className="text-xs leading-relaxed text-brand-dark/70">{source.detail}</p>
        {source.caveats && source.caveats.length > 0 && (
          <ul className="mt-2 space-y-1">
            {source.caveats.map((c) => {
              // Convention: a leading '!' marks a caveat that needs ACTION/confirmation;
              // everything else is informational context about how the number works.
              const action = c.startsWith('!');
              const text = action ? c.slice(1).trimStart() : c;
              return (
                <li
                  key={c}
                  className={`flex gap-1 text-[11px] ${action ? 'text-brand-amber' : 'text-brand-dark/55'}`}
                >
                  <span aria-hidden>{action ? '⚠' : '•'}</span>
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        )}
        {source.docUrl && (
          <a href={source.docUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[11px] text-brand-accent underline">
            API-dokumentation ↗
          </a>
        )}
      </div>
    </div>
  );
}
